/**
 * The calculator's frame — one card that sits as well on a partner's page, in
 * a modal over Citizen Web or in the app's sheet as on Tonnie's own page:
 *
 *   • the header: back, whose calculator it is (the portal's logo and name, or
 *     InOut's), the step, the host's own control (a language selector) and,
 *     in a modal, close. A logo is 32 px tall at its own width — a square mark
 *     beside the name, or a wordmark, which says the name itself (the name is
 *     then for screen readers);
 *   • the progress — "Step 2 of 4" and a bar — while there is a question;
 *   • one question at a time, or the answer;
 *   • the footer: what InOut is (arithmetic, never legal advice) and "Powered
 *     by Tonnie" — on every portal, whatever its theme.
 *
 * It is a size container: what is inside lays out by the frame's width
 * (`@xl:`), never the window's — a 460 px modal on a wide screen reads as a
 * phone does.
 *
 * Moving between screens is said: the body scrolls back to its top, the
 * focus goes to the new question's title and a screen reader hears "Step 2 of
 * 4: …" (the answer, its title). The page's landmarks — header, main,
 * footer — are the frame's only when it is the page (`standalone`: a
 * partner's portal, the app's sheet); inside Citizen Web it is a region of a
 * page that has its own.
 */
import { ArrowLeft, Hourglass, X } from '@tonnie/assets';
import { useLocale } from '@tonnie/locale';
import { Button, cn, Progress } from '@tonnie/ui';
import { useEffect, useRef, useState, type ReactNode } from 'react';

import type { CalculatorStep } from '../../hooks/use-calculator-wizard';
import { INOUT_TONE, INOUT_CLASSES } from '../../lib/inout-tone';

/** The portal the calculator runs in — its name and logo in the header. */
export interface CalculatorBrand {
  name: string;
  logoUrl: string | null;
}

/** A logo at least twice as wide as it is tall is a wordmark: it carries the name. */
const WORDMARK_ASPECT = 2;

interface CalculatorFrameProps {
  step: CalculatorStep;
  title: string;
  description?: ReactNode;
  /** Tonnie's own calculator when absent. */
  brand?: CalculatorBrand | null;
  onBack?: () => void;
  /** Beside the header — the host's control (a language selector). */
  headerEnd?: ReactNode;
  /** In a modal or a sheet: closing it. */
  onClose?: () => void;
  /** The frame is the page: its header, main and footer are the page's landmarks. */
  standalone?: boolean;
  children: ReactNode;
}

/** The four questions, in order — the answer is not a question. */
const QUESTIONS: readonly CalculatorStep[] = ['country', 'title', 'dates', 'trips'];

export function CalculatorFrame({
  step,
  title,
  description,
  brand,
  onBack,
  headerEnd,
  onClose,
  standalone = false,
  children,
}: CalculatorFrameProps) {
  const { t, formatNumber } = useLocale();
  // Measured once the logo loads — for that logo only: another (or none) is measured afresh.
  const logo = brand?.logoUrl ?? null;
  const [wordmarkOf, setWordmarkOf] = useState<string | null>(null);
  const wordmark = logo !== null && wordmarkOf === logo;
  const at = QUESTIONS.indexOf(step);
  const isQuestion = at >= 0;
  const stepText = isQuestion
    ? t('inout.calculator.stepOf', {
        step: formatNumber(at + 1),
        total: formatNumber(QUESTIONS.length),
      })
    : null;

  const bodyRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLHeadingElement>(null);
  const [said, setSaid] = useState('');
  const shown = useRef(step);
  useEffect(() => {
    // The first screen is where the person arrived: nothing to move or say.
    if (shown.current === step) return;
    shown.current = step;
    if (bodyRef.current) bodyRef.current.scrollTop = 0;
    titleRef.current?.focus({ preventScroll: true });
    setSaid(stepText ? t('inout.calculator.stepAnnouncement', { step: stepText, title }) : title);
  }, [step, stepText, title, t]);

  const Header = standalone ? 'header' : 'div';
  const Main = standalone ? 'main' : 'div';
  const Footer = standalone ? 'footer' : 'div';

  return (
    <div
      className="@container flex h-full min-h-0 w-full flex-col bg-background text-foreground"
      data-testid="inout-calculator"
      data-step={step}
    >
      <Header className="flex items-center gap-3 border-b border-border px-4 py-3 @xl:px-6">
        {onBack ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label={t('inout.calculator.back')}
            className="-ml-2 h-11 w-11 shrink-0 rounded-pill text-muted-foreground"
            data-testid="inout-calculator-back"
          >
            <ArrowLeft aria-hidden="true" />
          </Button>
        ) : null}
        <div className="flex min-w-0 flex-1 items-center gap-2.5">
          {logo ? (
            <img
              src={logo}
              alt=""
              onLoad={(event) => {
                const { naturalWidth, naturalHeight } = event.currentTarget;
                setWordmarkOf(naturalWidth >= WORDMARK_ASPECT * naturalHeight ? logo : null);
              }}
              className="h-8 w-auto max-w-40 shrink-0 rounded-lg object-contain object-left"
              data-testid="inout-calculator-brand-logo"
              data-wordmark={wordmark ? 'true' : 'false'}
            />
          ) : (
            <span
              className={cn(
                'flex h-8 w-8 shrink-0 items-center justify-center rounded-lg',
                INOUT_CLASSES.subtle,
                INOUT_CLASSES.text
              )}
            >
              <Hourglass className="h-4 w-4" aria-hidden="true" />
            </span>
          )}
          <div className="min-w-0">
            <p
              className={cn('truncate text-sm font-semibold', wordmark && 'sr-only')}
              data-testid="inout-calculator-brand"
            >
              {brand?.name ?? t('common.heroes.inout')}
            </p>
            <p className="truncate text-xs text-muted-foreground">{t('inout.calculator.name')}</p>
          </div>
        </div>
        {headerEnd}
        {onClose ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onClose}
            aria-label={t('common.close')}
            className="-mr-2 h-11 w-11 shrink-0 rounded-pill text-muted-foreground"
            data-testid="inout-calculator-close"
          >
            <X aria-hidden="true" />
          </Button>
        ) : null}
      </Header>

      <p className="sr-only" aria-live="polite" data-testid="inout-calculator-announcement">
        {said}
      </p>

      <Main ref={bodyRef} className="min-h-0 flex-1 overflow-y-auto px-4 py-5 @xl:px-6 @xl:py-6">
        <div className="mx-auto flex w-full max-w-3xl flex-col gap-5">
          {isQuestion && (
            <div className="flex flex-col gap-2" data-testid="inout-calculator-progress">
              <span className={cn('text-xs font-medium', INOUT_CLASSES.text)}>{stepText}</span>
              <Progress
                className="h-1.5"
                value={at + 1}
                max={QUESTIONS.length}
                tone={INOUT_TONE}
                aria-label={t('inout.calculator.progress')}
              />
            </div>
          )}
          <div className="flex flex-col gap-1.5">
            <h2
              ref={titleRef}
              tabIndex={-1}
              className="text-balance text-xl font-semibold focus:outline-none @xl:text-2xl"
            >
              {title}
            </h2>
            {description ? <p className="text-sm text-muted-foreground">{description}</p> : null}
          </div>
          {children}
        </div>
      </Main>

      <Footer className="flex flex-col gap-1 border-t border-border px-4 py-3 text-xs text-muted-foreground @xl:flex-row @xl:items-center @xl:justify-between @xl:px-6">
        <p className="min-w-0">{t('inout.clocks.disclaimer')}</p>
        <a
          href="https://tonnie.ai"
          target="_blank"
          rel="noopener noreferrer"
          className="shrink-0 whitespace-nowrap hover:text-foreground"
          data-testid="inout-calculator-powered-by"
        >
          {t('inout.calculator.poweredBy')}
        </a>
      </Footer>
    </div>
  );
}
